/** PATRON PAGES editor — the block editor for "How points work".
 *
 *  Everything here is a DRAFT. Nothing a venue types, uploads or reorders
 *  reaches a fan's phone until Save is pressed; the live preview draws the
 *  draft through the same `PatronPageView` the fan's phone uses.
 *
 *  Reused, not rebuilt: `ImageField` (upload + Brand library + the ask-first
 *  prompt), `BrandAssetPicker`, `designTokens` (fonts and text sizes) and the
 *  `btn` styles. Pictures are uploaded immediately — they have to be, to have a
 *  URL — but which picture a page USES is part of the draft.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowDown, ArrowUp, Loader2, Plus, Save, Smartphone, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { getToken } from '../../lib/session';
import { btn } from '../goalgame/admin/buttons';
import { ImageField } from '../goalgame/admin/ImageField';
import { BrandAssetPicker } from '../voucher_settings/BrandAssetPicker';
import { FONT_CHOICES, TEXT_SIZES } from '../../lib/designTokens';
import { PatronPageView } from './PatronPageView';

const field = "w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-2.5 py-1.5 text-sm";
const SIZES = [['S', 'Small'], ['M', 'Medium'], ['L', 'Large']];
const TOKENS = [
    ['{{program_name}}', 'patronEditor.programName'],
    ['{{points.attendance}}', 'Attendance points'],
    ['{{points.team_goal}}', 'Team goal points'],
    ['{{points.star_goal}}', 'Star player points'],
];

const uid = () => (crypto.randomUUID ? crypto.randomUUID() : String(Date.now() + Math.random()));

const NEW_BLOCK = {
    row: () => ({ id: uid(), type: 'row', props: {
        picture_url: '', picture_size: 'M', picture_pos: 'title',
        title_en: 'New row', title_fr: 'Nouvelle ligne',
        points_en: '', points_fr: '', body_en: '', body_fr: '' } }),
    number: () => ({ id: uid(), type: 'number', props: {
        value: '{{points.star_goal}}', label_en: 'points', label_fr: 'points' } }),
    button: () => ({ id: uid(), type: 'button', props: {
        label_en: 'Back to the game', label_fr: 'Retour au jeu', action: 'back' } }),
};

const Text = ({ label, value, onChange, testid, area = false }) => (
    <label className="block">
        <span className="block text-xs font-medium text-slate-500 mb-1">{label}</span>
        {area
            ? <textarea rows={3} className={field} value={value || ''} data-testid={testid}
                        onChange={(e) => onChange(e.target.value)} />
            : <input className={field} value={value || ''} data-testid={testid}
                     onChange={(e) => onChange(e.target.value)} />}
    </label>
);

const Colour = ({ label, value, onChange, testid }) => (
    <label className="block">
        <span className="block text-xs font-medium text-slate-500 mb-1">{label}</span>
        <div className="flex items-center gap-2">
            <input type="color" value={value || '#000000'} data-testid={testid}
                   onChange={(e) => onChange(e.target.value)}
                   className="h-9 w-12 rounded border border-slate-200 dark:border-slate-700 bg-transparent" />
            <input type="text" value={value || ''} onChange={(e) => onChange(e.target.value)}
                   className={`${field} font-mono`} />
        </div>
    </label>
);

export const PatronPagesBlock = ({ orgId }) => {
    const { t } = useTranslation();
    const [doc, setDoc] = useState(null);
    const [branding, setBranding] = useState(null);
    const [design, setDesign] = useState(null);
    const [titleEn, setTitleEn] = useState('');
    const [titleFr, setTitleFr] = useState('');
    const [blocks, setBlocks] = useState([]);
    const [values, setValues] = useState({});
    const [lang, setLang] = useState('en');
    const [picker, setPicker] = useState(null);
    const [busy, setBusy] = useState(false);
    const [dirty, setDirty] = useState(false);

    const load = useCallback(async () => {
        if (!orgId) return;
        try {
            const res = await api.get('/patron/admin/pages', { params: { organization_id: orgId } });
            const d = res.data;
            setDoc(d);
            setBranding(d.branding);
            setDesign(d.design);
            setValues(d.values || {});
            setTitleEn(d.pages.how_it_works.title_en);
            setTitleFr(d.pages.how_it_works.title_fr);
            setBlocks(d.pages.how_it_works.blocks);
            setDirty(false);
        } catch (_e) { toast.error('Could not load the pages'); }
    }, [orgId]);

    useEffect(() => { load(); }, [load]);

    const touch = () => setDirty(true);
    const setB = (k, v) => { setBranding((p) => ({ ...p, [k]: v })); touch(); };
    const setD = (k, v) => { setDesign((p) => ({ ...p, [k]: v })); touch(); };
    const setProp = (id, k, v) => {
        setBlocks((p) => p.map((b) => (b.id === id
            ? { ...b, props: { ...b.props, [k]: v } } : b)));
        touch();
    };
    const move = (i, dir) => {
        setBlocks((p) => {
            const next = [...p];
            const j = i + dir;
            if (j < 0 || j >= next.length) return p;
            [next[i], next[j]] = [next[j], next[i]];
            return next;
        });
        touch();
    };
    const add = (type) => { setBlocks((p) => [...p, NEW_BLOCK[type]()]); touch(); };
    const remove = (id) => { setBlocks((p) => p.filter((b) => b.id !== id)); touch(); };

    // The draft, in the exact shape the public page returns, so the preview is
    // the real fan page and not a lookalike.
    const preview = useMemo(() => {
        const resolve = (s) => String(s || '').replace(/\{\{\s*([a-z_.]+)\s*\}\}/g,
            (_m, key) => (values[key] ?? ''));
        const suffix = lang === 'fr' ? 'fr' : 'en';
        return {
            lang: suffix,
            title: resolve(suffix === 'fr' ? titleFr : titleEn),
            branding: branding || {},
            design: design || {},
            blocks: (blocks || []).map((b) => {
                const p = b.props || {};
                if (b.type === 'row') {
                    return { id: b.id, type: 'row', picture_url: p.picture_url,
                             picture_size: p.picture_size,
                             picture_pos: p.picture_pos || 'title',
                             title: resolve(p[`title_${suffix}`]),
                             points: resolve(p[`points_${suffix}`]),
                             body: resolve(p[`body_${suffix}`]) };
                }
                if (b.type === 'number') {
                    return { id: b.id, type: 'number', value: resolve(p.value),
                             label: resolve(p[`label_${suffix}`]) };
                }
                return { id: b.id, type: 'button', label: resolve(p[`label_${suffix}`]),
                         action: p.action };
            }),
        };
    }, [blocks, branding, design, titleEn, titleFr, lang, values]);

    const save = async () => {
        setBusy(true);
        try {
            const res = await api.put('/patron/admin/pages', {
                organization_id: orgId, page: 'how_it_works',
                title_en: titleEn, title_fr: titleFr,
                branding, design, blocks,
            });
            const d = res.data;
            setDoc(d);
            setValues(d.values || {});
            setDirty(false);
            toast.success('Page saved — the fans see it now');
        } catch (_e) { toast.error('Could not save the page'); }
        finally { setBusy(false); }
    };

    if (!doc || !branding || !design) {
        return <p className="text-sm text-slate-500 pt-3" data-testid="patron-pages-loading">…</p>;
    }

    const imageField = (label, hint, kind, urlKey, testid) => (
        <ImageField
            label={label} hint={hint} value={branding[urlKey]}
            apiPrefix="patron/admin" endpoint="pages/image" responseKey="url"
            extraFields={{ kind }} orgId={orgId}
            libraryKind={kind === 'wallpaper' ? 'wallpaper' : 'merchant_logo'}
            libraryName={label}
            onChange={(v) => setB(urlKey, v)}
            onLibrary={() => setPicker({ kind: kind === 'wallpaper' ? 'wallpaper' : 'merchant_logo',
                                         apply: (url) => setB(urlKey, url) })}
            testid={testid} />
    );

    return (
        <div className="space-y-5 pt-3" data-testid="patron-pages-editor">
            <div className="flex flex-wrap items-center gap-2">
                <p className="text-xs font-medium text-slate-500 grow">
                    Page A — "How points work". Nothing is live until you press Save.
                </p>
                {dirty && (
                    <span className="text-xs font-semibold text-amber-600" data-testid="patron-pages-dirty">
                        Unsaved changes
                    </span>
                )}
                <button type="button" onClick={save} disabled={busy}
                        data-testid="patron-pages-save" className={btn.primary}>
                    {busy ? <Loader2 className="animate-spin" size={15} /> : <Save size={15} />}
                    Save page
                </button>
            </div>

            <div className="grid lg:grid-cols-[1fr,320px] gap-6 items-start">
                <div className="space-y-5 min-w-0">
                    {/* logos, sizes, wallpaper */}
                    <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-3 space-y-4">
                        <p className="text-xs font-medium text-slate-500">
                            Logos and wallpaper (shared by every patron page)
                        </p>
                        {imageField(t('patronEditor.merchantLogo'), t('patronEditor.merchantLogoHint'), 'merchant_logo',
                                    'merchant_logo_url', 'pp-merchant-logo')}
                        <label className="block max-w-xs">
                            <span className="block text-xs font-medium text-slate-500 mb-1">
                                {t('patronEditor.merchantLogoSize')} — {branding.merchant_logo_px} px
                            </span>
                            <input type="range" min={20} max={240} value={branding.merchant_logo_px}
                                   onChange={(e) => setB('merchant_logo_px', Number(e.target.value))}
                                   data-testid="pp-merchant-logo-size" className="w-full" />
                        </label>
                        {imageField(t('patronEditor.programLogo'), t('patronEditor.programLogoHint'), 'program_logo',
                                    'program_logo_url', 'pp-program-logo')}
                        <label className="block max-w-xs">
                            <span className="block text-xs font-medium text-slate-500 mb-1">
                                {t('patronEditor.programLogoSize')} — {branding.program_logo_px} px
                            </span>
                            <input type="range" min={20} max={240} value={branding.program_logo_px}
                                   onChange={(e) => setB('program_logo_px', Number(e.target.value))}
                                   data-testid="pp-program-logo-size" className="w-full" />
                        </label>
                        {imageField(t('patronEditor.wallpaper'), t('patronEditor.wallpaperHint'), 'wallpaper',
                                    'wallpaper_url', 'pp-wallpaper')}
                        <label className="block max-w-xs">
                            <span className="block text-xs font-medium text-slate-500 mb-1">
                                {t('patronEditor.wallpaperDark')} — {branding.wallpaper_dark}%
                            </span>
                            <input type="range" min={0} max={90} value={branding.wallpaper_dark}
                                   onChange={(e) => setB('wallpaper_dark', Number(e.target.value))}
                                   data-testid="pp-wallpaper-dark" className="w-full" />
                        </label>
                        {/* The wallpaper's OWN sizing. Nothing here touches a logo. */}
                        <div className="grid sm:grid-cols-2 gap-3 max-w-xl">
                            <label className="block">
                                <span className="block text-xs font-medium text-slate-500 mb-1">
                                    {t('patronEditor.wallpaperFit')}
                                </span>
                                <select className={field} value={branding.wallpaper_fit || 'fill'}
                                        data-testid="pp-wallpaper-fit"
                                        onChange={(e) => setB('wallpaper_fit', e.target.value)}>
                                    <option value="fill">{t('patronEditor.fitFill')}</option>
                                    <option value="whole">{t('patronEditor.fitWhole')}</option>
                                </select>
                            </label>
                            <label className="block">
                                <span className="block text-xs font-medium text-slate-500 mb-1">
                                    {t('patronEditor.wallpaperZoom')} — {branding.wallpaper_zoom || 100}%
                                </span>
                                <input type="range" min={100} max={200}
                                       value={branding.wallpaper_zoom || 100}
                                       onChange={(e) => setB('wallpaper_zoom', Number(e.target.value))}
                                       data-testid="pp-wallpaper-zoom" className="w-full" />
                            </label>
                        </div>
                        <div className="max-w-xs">
                            <span className="block text-xs font-medium text-slate-500 mb-1">
                                {t('patronEditor.wallpaperPosition')}
                            </span>
                            <div className="grid grid-cols-3 gap-1 w-[108px]"
                                 data-testid="pp-wallpaper-position">
                                {[0, 50, 100].map((y) => [0, 50, 100].map((x) => {
                                    const on = (branding.wallpaper_x ?? 50) === x
                                        && (branding.wallpaper_y ?? 50) === y;
                                    return (
                                        <button key={`${x}-${y}`} type="button"
                                                aria-label={`${x}% ${y}%`}
                                                data-testid={`pp-wallpaper-pos-${x}-${y}`}
                                                onClick={() => { setB('wallpaper_x', x); setB('wallpaper_y', y); }}
                                                className={`h-8 w-8 rounded-md border transition-colors ${
                                                    on ? 'border-emerald-500 bg-emerald-500/20'
                                                       : 'border-slate-300 dark:border-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800'}`} />
                                    );
                                }))}
                            </div>
                        </div>
                    </div>

                    {/* colours, fonts, sizes */}
                    <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-3 space-y-3">
                        <p className="text-xs font-medium text-slate-500">Colours and type</p>
                        <div className="grid sm:grid-cols-4 gap-3">
                            <Colour label="Background" value={design.bg}
                                    onChange={(v) => setD('bg', v)} testid="pp-colour-bg" />
                            <Colour label="Text" value={design.text}
                                    onChange={(v) => setD('text', v)} testid="pp-colour-text" />
                            <Colour label="Accent (the points)" value={design.accent}
                                    onChange={(v) => setD('accent', v)} testid="pp-colour-accent" />
                            <Colour label="Button" value={design.button}
                                    onChange={(v) => setD('button', v)} testid="pp-colour-button" />
                        </div>
                        <div className="grid sm:grid-cols-4 gap-3">
                            <label className="block">
                                <span className="block text-xs font-medium text-slate-500 mb-1">Headline font</span>
                                <select className={field} value={design.headline_font}
                                        data-testid="pp-headline-font"
                                        onChange={(e) => setD('headline_font', e.target.value)}>
                                    {FONT_CHOICES.map((f) => (
                                        <option key={f.key || 'default'} value={f.key}>{f.label}</option>
                                    ))}
                                </select>
                            </label>
                            <label className="block">
                                <span className="block text-xs font-medium text-slate-500 mb-1">Body font</span>
                                <select className={field} value={design.body_font}
                                        data-testid="pp-body-font"
                                        onChange={(e) => setD('body_font', e.target.value)}>
                                    {FONT_CHOICES.map((f) => (
                                        <option key={f.key || 'default'} value={f.key}>{f.label}</option>
                                    ))}
                                </select>
                            </label>
                            <label className="block">
                                <span className="block text-xs font-medium text-slate-500 mb-1">Headline size</span>
                                <select className={field} value={design.headline_size}
                                        data-testid="pp-headline-size"
                                        onChange={(e) => setD('headline_size', e.target.value)}>
                                    {TEXT_SIZES.filter((s) => s.key).map((s) => (
                                        <option key={s.key} value={s.key}>{s.label}</option>
                                    ))}
                                </select>
                            </label>
                            <label className="block">
                                <span className="block text-xs font-medium text-slate-500 mb-1">Body size</span>
                                <select className={field} value={design.body_size}
                                        data-testid="pp-body-size"
                                        onChange={(e) => setD('body_size', e.target.value)}>
                                    {TEXT_SIZES.filter((s) => s.key).map((s) => (
                                        <option key={s.key} value={s.key}>{s.label}</option>
                                    ))}
                                </select>
                            </label>
                        </div>
                    </div>

                    {/* the page title */}
                    <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-3 grid sm:grid-cols-2 gap-3">
                        <Text label="Page title EN" value={titleEn}
                              onChange={(v) => { setTitleEn(v); touch(); }} testid="pp-title-en" />
                        <Text label="Page title FR" value={titleFr}
                              onChange={(v) => { setTitleFr(v); touch(); }} testid="pp-title-fr" />
                        <p className="sm:col-span-2 text-[11px] text-slate-500">
                            Live values you can type into any text:{' '}
                            {TOKENS.map(([tok, label]) => `${tok} = ${label.startsWith('patronEditor.') ? t(label) : label}`).join(' · ')}.
                            They are replaced with tonight's real numbers — never type a number.
                        </p>
                    </div>

                    {/* the blocks */}
                    <div className="space-y-3" data-testid="patron-pages-blocks">
                        {blocks.map((b, i) => (
                            <div key={b.id} className="rounded-xl border border-slate-200 dark:border-slate-700 p-3 space-y-3"
                                 data-testid={`pp-block-${i}`}>
                                <div className="flex items-center gap-2">
                                    <span className="text-xs font-semibold uppercase tracking-wider text-slate-500 grow"
                                          data-testid={`pp-block-type-${i}`}>
                                        {b.type === 'row' ? 'Picture + title + text'
                                            : b.type === 'number' ? 'Big live number' : 'Button'}
                                    </span>
                                    <button type="button" onClick={() => move(i, -1)} disabled={i === 0}
                                            data-testid={`pp-block-up-${i}`} className={btn.smallSecondary}>
                                        <ArrowUp size={13} />
                                    </button>
                                    <button type="button" onClick={() => move(i, 1)}
                                            disabled={i === blocks.length - 1}
                                            data-testid={`pp-block-down-${i}`} className={btn.smallSecondary}>
                                        <ArrowDown size={13} />
                                    </button>
                                    <button type="button" onClick={() => remove(b.id)}
                                            data-testid={`pp-block-remove-${i}`}
                                            className={`${btn.iconDanger} px-3 py-1.5`}>
                                        <Trash2 size={13} />
                                    </button>
                                </div>

                                {b.type === 'row' && (
                                    <>
                                        <ImageField
                                            label={t('patronEditor.picture')} hint={t('patronEditor.pictureHint')}
                                            value={b.props.picture_url}
                                            apiPrefix="patron/admin" endpoint="pages/image"
                                            responseKey="url"
                                            extraFields={{ kind: 'block', block_id: b.id }}
                                            orgId={orgId} libraryKind="merchant_logo"
                                            libraryName="Page picture"
                                            onChange={(v) => setProp(b.id, 'picture_url', v)}
                                            onLibrary={() => setPicker({ kind: 'merchant_logo',
                                                apply: (url) => setProp(b.id, 'picture_url', url) })}
                                            testid={`pp-block-picture-${i}`} />
                                        <div className="flex flex-wrap gap-3">
                                            <label className="block max-w-[180px]">
                                                <span className="block text-xs font-medium text-slate-500 mb-1">{t('patronEditor.pictureSize')}</span>
                                                <select className={field} value={b.props.picture_size}
                                                        data-testid={`pp-block-picture-size-${i}`}
                                                        onChange={(e) => setProp(b.id, 'picture_size', e.target.value)}>
                                                    {SIZES.map(([k, label]) => (
                                                        <option key={k} value={k}>{label}</option>
                                                    ))}
                                                </select>
                                            </label>
                                            <label className="block max-w-[220px]">
                                                <span className="block text-xs font-medium text-slate-500 mb-1">{t('patronEditor.picturePosition')}</span>
                                                <select className={field}
                                                        value={b.props.picture_pos || 'title'}
                                                        data-testid={`pp-block-picture-pos-${i}`}
                                                        onChange={(e) => setProp(b.id, 'picture_pos', e.target.value)}>
                                                    <option value="title">{t('patronEditor.posTitle')}</option>
                                                    <option value="above">{t('patronEditor.posAbove')}</option>
                                                    <option value="all">{t('patronEditor.posAll')}</option>
                                                </select>
                                            </label>
                                        </div>
                                        <div className="grid sm:grid-cols-2 gap-3">
                                            <Text label="Title EN" value={b.props.title_en}
                                                  onChange={(v) => setProp(b.id, 'title_en', v)}
                                                  testid={`pp-block-title-en-${i}`} />
                                            <Text label="Title FR" value={b.props.title_fr}
                                                  onChange={(v) => setProp(b.id, 'title_fr', v)}
                                                  testid={`pp-block-title-fr-${i}`} />
                                            <Text label="Points line EN" value={b.props.points_en}
                                                  onChange={(v) => setProp(b.id, 'points_en', v)}
                                                  testid={`pp-block-points-en-${i}`} />
                                            <Text label="Points line FR" value={b.props.points_fr}
                                                  onChange={(v) => setProp(b.id, 'points_fr', v)}
                                                  testid={`pp-block-points-fr-${i}`} />
                                            <Text label="Text EN" area value={b.props.body_en}
                                                  onChange={(v) => setProp(b.id, 'body_en', v)}
                                                  testid={`pp-block-body-en-${i}`} />
                                            <Text label="Text FR" area value={b.props.body_fr}
                                                  onChange={(v) => setProp(b.id, 'body_fr', v)}
                                                  testid={`pp-block-body-fr-${i}`} />
                                        </div>
                                    </>
                                )}

                                {b.type === 'number' && (
                                    <div className="grid sm:grid-cols-3 gap-3">
                                        <Text label="Live value" value={b.props.value}
                                              onChange={(v) => setProp(b.id, 'value', v)}
                                              testid={`pp-block-value-${i}`} />
                                        <Text label="Label EN" value={b.props.label_en}
                                              onChange={(v) => setProp(b.id, 'label_en', v)}
                                              testid={`pp-block-label-en-${i}`} />
                                        <Text label="Label FR" value={b.props.label_fr}
                                              onChange={(v) => setProp(b.id, 'label_fr', v)}
                                              testid={`pp-block-label-fr-${i}`} />
                                    </div>
                                )}

                                {b.type === 'button' && (
                                    <div className="grid sm:grid-cols-2 gap-3">
                                        <Text label="Label EN" value={b.props.label_en}
                                              onChange={(v) => setProp(b.id, 'label_en', v)}
                                              testid={`pp-block-label-en-${i}`} />
                                        <Text label="Label FR" value={b.props.label_fr}
                                              onChange={(v) => setProp(b.id, 'label_fr', v)}
                                              testid={`pp-block-label-fr-${i}`} />
                                    </div>
                                )}
                            </div>
                        ))}

                        <div className="flex flex-wrap gap-2">
                            <button type="button" onClick={() => add('row')}
                                    data-testid="pp-add-row" className={btn.smallSecondary}>
                                <Plus size={13} /> Picture + title + text
                            </button>
                            <button type="button" onClick={() => add('number')}
                                    data-testid="pp-add-number" className={btn.smallSecondary}>
                                <Plus size={13} /> Big live number
                            </button>
                            <button type="button" onClick={() => add('button')}
                                    data-testid="pp-add-button" className={btn.smallSecondary}>
                                <Plus size={13} /> Button
                            </button>
                        </div>
                    </div>
                </div>

                {/* the phone preview */}
                <div className="space-y-3" data-testid="pp-preview">
                    <div className="flex items-center gap-2 text-xs font-medium text-slate-500">
                        <Smartphone size={14} /> Live preview — nothing is saved
                    </div>
                    <div className="flex gap-1.5">
                        {['en', 'fr'].map((code) => (
                            <button key={code} type="button" onClick={() => setLang(code)}
                                    data-testid={`pp-preview-lang-${code}`}
                                    className={`rounded-full px-3 py-1 text-xs font-semibold border ${
                                        lang === code
                                            ? 'bg-slate-900 text-white border-slate-900 dark:bg-white dark:text-slate-900'
                                            : 'border-slate-300 text-slate-600 dark:border-slate-600 dark:text-slate-300'}`}>
                                {code.toUpperCase()}
                            </button>
                        ))}
                    </div>
                    <div className="rounded-[2rem] border-[7px] border-slate-900 dark:border-slate-700 bg-slate-900 shadow-xl overflow-hidden w-[300px]"
                         data-testid="pp-preview-frame">
                        <div className="overflow-y-auto" data-testid="pp-preview-screen"
                             style={{ height: 'clamp(380px, calc(100vh - 16rem), 620px)' }}>
                            <PatronPageView page={preview} framed />
                        </div>
                    </div>
                    {doc.card_id && (
                        <a href={`/patron/${doc.card_id}/how-points-work`} target="_blank" rel="noreferrer"
                           data-testid="pp-open-live"
                           className="text-xs underline text-slate-500">Open the live page</a>
                    )}
                </div>
            </div>

            <BrandAssetPicker
                open={!!picker}
                onOpenChange={(v) => { if (!v) setPicker(null); }}
                kind={picker?.kind}
                orgId={orgId}
                token={getToken()}
                isSuperAdmin
                onApply={(url) => { picker?.apply?.(url); setPicker(null); }}
            />
        </div>
    );
};

export default PatronPagesBlock;
